import { IEarlyDriveData, ILineSet } from "@/domain/entities/lineInfo";
import { ParseError } from "@/repositories/shared/errors/parseError";
import { injectable } from "inversify";
import { err, ok, Result } from "neverthrow";
import {
  HttpBusStop,
  HttpEarlyDriveInfo,
  HttpLineSet,
  HttpOnBoarLineData,
  IOnBoardLineParser,
} from "../lineInfoRepository.types";
import {
  IOnBoardLine,
  IOnBoardLineData,
} from "@/domain/entities/onBoardLine/onBoardLine.types";
import { OnBoardLine } from "@/domain/entities/onBoardLine/onBoardLine";
import { BusStop, IBusStop, IBusStopData } from "@/domain/entities/busStop";
import { WeekdayCode, WeekdayCodeName } from "@/types";
import dayjs from "dayjs";

function parseBusStopList(
  stations: Array<HttpBusStop>,
  onStationUid: string
): IBusStop[] {
  const busStops: IBusStop[] = [];

  // 화면 표시 안하는 정류장 필터링
  const filteredStations = stations?.filter((station) => station.displayYn);

  filteredStations?.map((station: HttpBusStop, index: number) => {
    const busStopData: IBusStopData = {
      id: station.uid,
      number: station.no,
      name: station.name,
      shortName: station.shortName,
      order: index,
      departureTime: station.departureTime?.trim(),
      arrivalTime: station.arrivalTime?.trim(),
      isFirstStop: station.beginYn,
      isLastStop: station.endYn,
      isGetOnStop: station.onYn,
      isGetOffStop: station.offYn,
      isTransitStop: station.transYn,
      isReturnStop: station.returnYn,
      isDisplayStop: station.displayYn,
      isOmitted: station.omittedYn,
      isTemp: station.tempYn,
      shouldShowStreetView: station.streetViewYn,
      shouldShowBusStopLayout: station.useStationLayoutYn,
      latitude: station.lat,
      longitude: station.lng,
      direction: "",
      isMyBoardingStop: station.uid === onStationUid,
      transferBusStops: station.trans?.routes,
      transferGuide: station.trans?.transText,
      isFavorite: station.isFavorite ?? false,
      useBeginDtm: dayjs(station.useBeginDtm).format("YYYY-MM-DD HH:mm"),
      useEndDtm: station.useEndDtm
        ? dayjs(station.useEndDtm).format("YYYY-MM-DD HH:mm")
        : undefined,
    };

    busStops.push(new BusStop(busStopData));
  });
  return busStops;
}

function findBusStopById(id: string, busStops: Array<IBusStop>) {
  return busStops.find((busStop) => busStop.id === id);
}

function convertWeekdayName(weekdayCode: string): WeekdayCodeName {
  const weekdayCodeMap: Record<string, WeekdayCodeName> = {
    [WeekdayCode.MON]: WeekdayCodeName.MON,
    [WeekdayCode.TUE]: WeekdayCodeName.TUE,
    [WeekdayCode.WED]: WeekdayCodeName.WED,
    [WeekdayCode.THU]: WeekdayCodeName.THU,
    [WeekdayCode.FRI]: WeekdayCodeName.FRI,
    [WeekdayCode.SAT]: WeekdayCodeName.SAT,
    [WeekdayCode.SUN]: WeekdayCodeName.SUN,
    [WeekdayCode.HOLI]: WeekdayCodeName.HOLI,
  };

  return weekdayCodeMap[weekdayCode] ?? WeekdayCodeName.NONE;
}

function sortByWeekday(earlyDriveInfos: IEarlyDriveData[]): IEarlyDriveData[] {
  // 요일에 따라 정렬(월,화,수,목,금,토,일,휴 순)
  const sortedWeekDay = Object.values(WeekdayCode);

  const sortedEarlyDriveInfos = earlyDriveInfos?.sort((a, b) => {
    const indexOfA = sortedWeekDay.findIndex(
      (weekday) => weekday === a.weekdayCode
    );
    const indexOfB = sortedWeekDay.findIndex(
      (weekday) => weekday === b.weekdayCode
    );
    return indexOfA - indexOfB;
  });

  return sortedEarlyDriveInfos;
}

function parseEarlyDriveInfo(
  earlyDriveInfo: HttpEarlyDriveInfo
): IEarlyDriveData {
  const parseEarlyDriveInfo = {
    weekdayCode: earlyDriveInfo.weekdayCode,
    weekdayName: convertWeekdayName(earlyDriveInfo.weekdayCode),
    earlyMinute: earlyDriveInfo.earlyMinute,
  };
  return parseEarlyDriveInfo;
}

function parseLineSet(lineSet: HttpLineSet): ILineSet {
  return {
    lineSetCodeUid: lineSet.lineSetCodeuid,
    name: lineSet.name,
    displayText: lineSet.shortName ? lineSet.shortName : lineSet.name,
    isToday: lineSet.todayYn === "Y",
  };
}

@injectable()
export class OnBoardLineParser implements IOnBoardLineParser {
  toDomain(data: HttpOnBoarLineData): Result<IOnBoardLineData, ParseError> {
    try {
      const { onBoardInfo, fullSeatLineRouteCount } = data;

      const parsedFullSeatCount = {
        fullSeatLineRouteCount: fullSeatLineRouteCount ?? 0,
      };

      if (!onBoardInfo) {
        return ok(parsedFullSeatCount);
      }

      const busStops = parseBusStopList(
        onBoardInfo.stations ?? [],
        onBoardInfo.expectedOnStationUid ?? ""
      );
      const sortedEarlyDriveInfos = sortByWeekday(
        onBoardInfo.earlyDriveWeekly?.map(parseEarlyDriveInfo)
      );

      const onBoardLineData: IOnBoardLine = {
        serviceUid: onBoardInfo.serviceUid,
        memberUid: onBoardInfo.memberUid,
        allocUid: onBoardInfo.allocUid,
        boardUid: onBoardInfo.boardUid,
        lineRouteUid: onBoardInfo.lineRouteUid,
        lineRouteNo: onBoardInfo.lineRouteNo,
        lineRouteName: onBoardInfo.lineRouteName,
        lineTurnUid: onBoardInfo.lineTurnUid,
        lineTypeCode: onBoardInfo.lineTypeCode,
        lineTypeName: onBoardInfo.lineTypeName,
        campusCode: onBoardInfo.campusCode,
        campusName: onBoardInfo.campusName,
        lineDirectionCode: onBoardInfo.lineDirectionCode,
        lineDirectionName: onBoardInfo.lineDirectionName,
        lineDistrictCode: onBoardInfo.lineDistrictCode,
        lineDistrictName: onBoardInfo.lineDistrictName,
        lineTimeCode: onBoardInfo.lineTimeCode,
        lineTimeName: onBoardInfo.lineTimeName,
        firstBusStop: findBusStopById(
          onBoardInfo.beginLineStationUid,
          busStops
        )!,
        lastBusStop: findBusStopById(onBoardInfo.endLineStationUid, busStops)!,
        getOnBusStop: onBoardInfo.expectedOnStationUid
          ? findBusStopById(onBoardInfo.expectedOnStationUid, busStops)
          : undefined,
        getOffBusStop: onBoardInfo.expectedOffStationUid
          ? findBusStopById(onBoardInfo.expectedOffStationUid, busStops)
          : undefined,
        realLastBusStopDateTime: onBoardInfo.realEndDtm,
        realGetOnBusStopId: onBoardInfo.realOnStationUid,
        realGetOnBusStopName: onBoardInfo.realOnStationName,
        realGetOnDateTime: onBoardInfo.realOnDtm,
        carNo: onBoardInfo.carNo,
        busStops: busStops,
        earlyDriveInfos: sortedEarlyDriveInfos,
        lineSets: onBoardInfo.lineSets?.map(parseLineSet) ?? [],
        requestGetOffBusStopId: onBoardInfo.requestOffStationUid,
        detourBeginDtm: onBoardInfo.detourBeginDtm,
        detourEndDtm: onBoardInfo.detourEndDtm,
        detourReason: onBoardInfo.detourReason,
      };
      const onBoardLine = new OnBoardLine(onBoardLineData);
      return ok({
        onBoardLine,
        ...parsedFullSeatCount,
      });
    } catch (error: any) {
      return err(new ParseError(error.message));
    }
  }
}
